import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PandlPasswordModal from '../popupmodal/PandlPasswordModal';

/**
 * DeRightSidebar — 4-button action panel for Edit Data Entry mode.
 *
 * Matches the old software's right sidebar pattern:
 *   - Vertical stack of full-width action buttons
 *   - Active button highlighted (matches activeStep from timeline)
 *   - Clicking navigates to the target route, passing id in route state
 *
 * P&L Protection:
 *   Clicking "P & L" opens PandlPasswordModal.
 *   Only navigates to /business/pl/:id if the correct password is entered.
 *   Password is validated against sessionStorage.pandlPassword (from login response).
 *   Fallback static password: '1234'.
 *
 * No Redux — id comes from the page (useParams) via prop.
 *
 * Props:
 *   id          {string|number}  The data entry record id
 *   activeStep  {number}         1–5 — which button is highlighted
 *   onStepChange {function}      Called with new step number when button is clicked
 */
const BUTTONS = [
  { step: 1, label: 'COPY DOCUMENT',    route: (id) => `/business/copy-document/${id}`,  protected: false },
  { step: 2, label: 'CLEARING CHARGES', route: (id) => `/business/clearing-charges/${id}`, protected: false },
  { step: 3, label: 'EWAY',             route: (id) => `/business/eway/${id}`,             protected: false },
  { step: 4, label: 'P & L',            route: (id) => `/business/pl/${id}`,               protected: true  },
  { step: 5, label: 'FILE DOCUMENT',    route: (id) => `/business/file-document/${id}`,    protected: false },
  { step: 6, label: 'BANK DETAILS',     route: (id) => `/business/bank-details/${id}`,     protected: false },
];

const DeRightSidebar = ({ id, activeStep = 1, onStepChange }) => {
  const navigate = useNavigate();

  // P&L password modal state
  const [pandlModalOpen, setPandlModalOpen] = useState(false);
  // Hold the pending P&L navigation target until password is verified
  const pendingPandlRoute = `/business/pl/${id}`;

  const handleClick = (btn) => {
    if (btn.protected) {
      // Open password gate — do NOT navigate yet
      setPandlModalOpen(true);
      return;
    }
    if (onStepChange) onStepChange(btn.step);
    navigate(btn.route(id), { state: { id } });
  };

  const handlePandlSuccess = () => {
    setPandlModalOpen(false);
    if (onStepChange) onStepChange(4);
    navigate(pendingPandlRoute, { state: { id } });
  };

  const handlePandlClose = () => {
    setPandlModalOpen(false);
  };

  return (
    <>
      <div className="card shadow-sm border rounded-3 p-3 bg-white h-100">
        <div className="de-right-sidebar d-flex flex-column gap-2">
          {BUTTONS.map((btn) => (
            <button
              key={btn.step}
              type="button"
              id={`de-sidebar-btn-${btn.step}`}
              className={`de-sidebar-btn${activeStep === btn.step ? ' active' : ''}`}
              onClick={() => handleClick(btn)}
            >
              {btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* P&L Password Gate Modal */}
      <PandlPasswordModal
        isOpen={pandlModalOpen}
        onClose={handlePandlClose}
        onSuccess={handlePandlSuccess}
      />
    </>
  );
};

export default DeRightSidebar;
